import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "@phosphor-icons/react";

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const panelRef = useRef(null);

  const refresh = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) api.post("/notifications/read", { ids: [n.id] }).then(refresh).catch(() => {});
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read", { all: true });
    refresh();
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        className="btn-ghost relative"
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <Bell size={18} weight="bold" />
        {data.unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#dfff00] text-black rounded-full text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-badge"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-[#121212] border border-[#27272a] rounded-md shadow-xl z-50"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272a]">
            <span className="font-label">Notifications</span>
            {data.unread > 0 && (
              <button
                onClick={markAll}
                className="text-[10px] uppercase tracking-wider font-bold text-[#dfff00] hover:underline"
                data-testid="notifications-mark-all"
              >
                <Check size={10} weight="bold" className="inline mr-1" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#27272a]">
            {data.items.length === 0 && (
              <div className="p-4 text-xs text-zinc-500" data-testid="notifications-empty">
                No notifications yet.
              </div>
            )}
            {data.items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-3 py-2.5 hover:bg-zinc-800/60 ${n.read ? "opacity-60" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#dfff00] mt-1.5 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{n.title}</div>
                    <div className="text-xs text-zinc-400">{n.body}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
